// src/pages/Admin/views/verification-rules/BannedEmailsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  listBannedEmails,
  batchUpdateBannedEmails
} from './verificationRulesApi' // Adjust path as needed if apiServices.js is elsewhere
import CrudTable from '../../../../components/Admin/VerificationRules/CrudTable'; // Adjust path
import { formatDate, formatDateTime } from '../../../../utility/dateFormatter'; // Adjust path
import { Button } from '../../../../components/ui/button'; // Assuming Button.jsx is in src/components/
import { useToast } from '../../../../hooks/use-toast';

const BannedEmailsPage = () => {
  const [allBannedEmails, setAllBannedEmails] = useState([]); // Holds all fetched emails
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const { toast } = useToast();

  // Unified list for pending changes: new items, updated items (including deactivations)
  // Each item in pendingChanges will have a 'status': 'new', 'updated', 'deactivated'
  // Or simply, new items are those without an 'id', updated items are those with an 'id'.
  // The backend batch update takes List[Dict], so it can differentiate.
  const [stagedChanges, setStagedChanges] = useState([]);
  // Staged changes will be an array of email objects ready to be sent to the batch endpoint.
  // New items won't have an 'id'. Existing items will have an 'id'.

  const [isAdding, setIsAdding] = useState(false);
  const [newItemData, setNewItemData] = useState({
    email_address: '',
    reason: '',
    is_fatal: true,
    is_active: true, // New items default to active
    expires_at: '',
  });

  const [editingItemId, setEditingItemId] = useState(null);
  const [currentItemDataForEdit, setCurrentItemDataForEdit] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');
    try {
      const response = await listBannedEmails();
      setAllBannedEmails(response.data || []); // Set the master list
      setStagedChanges([]); // Clear any previous staged changes on fresh fetch
    } catch (err) {
  console.error("Fetch Error Raw:", err.response || err);
  let errorMessage = "Failed to fetch banned emails.";
  if (err.response && err.response.data && err.response.data.detail) {
    if (typeof err.response.data.detail === 'string') {
      errorMessage = err.response.data.detail;
    } else {
      // Handle array or object details if necessary, similar to above
      errorMessage = JSON.stringify(err.response.data.detail);
    }
  } else if (err.message) {
    errorMessage = `Failed to fetch banned emails: ${err.message}`;
  }
  setError(errorMessage);
} finally {
      setIsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);


  // Clear edit mode if the item being edited is removed (e.g., after save)
  useEffect(() => {
    if (editingItemId && !allBannedEmails.find(item => (item.id || item.tempId) === editingItemId)) {
      setEditingItemId(null);
      setCurrentItemDataForEdit(null);
    }
  }, [allBannedEmails, editingItemId]);

  const stageChange = (itemDataWithChange) => {
    setStagedChanges(prevChanges => {
        const keyOfChangedItem = itemDataWithChange.id || itemDataWithChange.tempId;
        let itemExistsInStaged = false;

        const updatedChanges = prevChanges.map(existingChange => {
            const keyOfExistingChange = existingChange.id || existingChange.tempId;
            if (keyOfExistingChange === keyOfChangedItem) {
                itemExistsInStaged = true;
                // Merge, ensuring itemDataWithChange (which is more complete) overwrites
                return { ...existingChange, ...itemDataWithChange };
            }
            return existingChange;
        });

        if (!itemExistsInStaged) {
            // If itemDataWithChange has a real id, it's an existing item from server.
            // If it only has tempId, it's a new item.
            // The 'isNew' flag should be set correctly on itemDataWithChange before calling stageChange.
            return [...updatedChanges, { ...itemDataWithChange }];
        }
        return updatedChanges;
    });
    };

  const handleEditClick = (item) => {

    const currentItemState = allBannedEmails.find(i => (i.id || i.tempId) === (item.id || item.tempId)) || item;
    if (editingItemId === (currentItemState.id || currentItemState.tempId)) {
      handleCancelEdit();
    } else {
      setEditingItemId(currentItemState.id || currentItemState.tempId);
      const dataForEditing = {
        ...currentItemState, // Base on the current state in allBannedEmails
        expires_at: currentItemState.expires_at ? formatDate(currentItemState.expires_at) : '',
      };
      // Check if there's a more recent version in stagedChanges (e.g., deactivated, then edit)
      const stagedVersion = stagedChanges.find(sc => (sc.id || sc.tempId) === (currentItemState.id || currentItemState.tempId));
      if (stagedVersion) {
          setCurrentItemDataForEdit({ // Prioritize staged version for consistency if editing an already modified item
              ...dataForEditing, // to get formatted expires_at initially
              ...stagedVersion, // then overlay all staged fields
              expires_at: stagedVersion.expires_at ? formatDate(stagedVersion.expires_at) : '', // re-format expires_at from staged
          });
      } else {
          setCurrentItemDataForEdit(dataForEditing);
      }
    }
    setSuccessMessage('');
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setCurrentItemDataForEdit(null);
  };

  const handleInputChange = (e) => {
    setSuccessMessage('');
    const { name, value, type, checked } = e.target;
    const L_value = type === 'checkbox' ? checked : value;

    if (editingItemId) {
      setCurrentItemDataForEdit(prev => ({ ...prev, [name]: L_value }));
    } else if (isAdding) {
      setNewItemData(prev => ({ ...prev, [name]: L_value }));
    }
  };

  const applyRowEditChanges = () => {
    if (!editingItemId || !currentItemDataForEdit) return;

    const editedData = { ...currentItemDataForEdit };
    // Determine if it's a new item based on presence of tempId and absence of id
    editedData.isNew = !!(editedData.tempId && !editedData.id);
    if (editedData.id && typeof editedData.id === 'number') { // Persisted item
        delete editedData.tempId; // Clean up tempId if real id exists
    }


    // Update allBannedEmails for immediate UI reflection
    setAllBannedEmails(prevAll =>
      prevAll.map(email =>
        (email.id === editingItemId || email.tempId === editingItemId)
          ? { ...email, ...editedData } // Apply all changes from edit form
          : email
      )
    );
    stageChange(editedData); // Stage the complete, edited data
    setSuccessMessage('Changes for the row applied. Click "Save All Changes" to persist.');
    // Optionally, exit edit mode:
    // handleCancelEdit();
  };

  const handleAddNewRowToList = () => {
    // ... (validation)
    if (!newItemData.email_address) { setError("Email address is required."); return; }
    if (!/\S+@\S+\.\S+/.test(newItemData.email_address)) { setError("Valid email required."); return; }
    setError(null);

    const tempId = `temp-${Date.now()}`;
    const newEmailEntry = {
      ...newItemData,
      tempId: tempId,
      isNew: true, // Mark as new
      expires_at: newItemData.expires_at || null,
      // id: undefined, // Explicitly no 'id' for new items
    };
    delete newEmailEntry.id; // Ensure no 'id' field for new items initially

    setAllBannedEmails(prev => [newEmailEntry, ...prev]); // Add to main display list
    stageChange(newEmailEntry); // Stage this new item

    setNewItemData({ email_address: '', reason: '', is_fatal: true, is_active: true, expires_at: '' });
    setIsAdding(false);
    setSuccessMessage('Email added to list. Click "Save All Changes" to persist.');
  };

  // "Delete" now means deactivate (set is_active: false)
  const handleDeleteOrDeactivate = (itemToToggle) => {
    const itemCurrentState = allBannedEmails.find(i => (i.id || i.tempId) === (itemToToggle.id || itemToToggle.tempId)) || itemToToggle;

    if (window.confirm(`Are you sure you want to ${itemCurrentState.is_active ? 'deactivate' : 'activate'} "${itemCurrentState.email_address || 'this item'}"? This will be saved on "Save All".`)) {
      setSuccessMessage('');
      setError(null);

      const newActiveState = !itemCurrentState.is_active;

      // Update the item in the main display list (allBannedEmails)
      setAllBannedEmails(prevAllEmails =>
        prevAllEmails.map(email => {
          const emailKey = email.id || email.tempId;
          const itemToToggleKey = itemCurrentState.id || itemCurrentState.tempId;
          if (emailKey === itemToToggleKey) {
            return { ...email, is_active: newActiveState }; // Correctly updates only the specific item
          }
          return email;
        })
      );

      // Prepare the data for staging (this is what gets sent to backend eventually)
      const changeToStage = {
        ...itemCurrentState, // Start with the current state of the item
        is_active: newActiveState,
        isNew: !itemCurrentState.id, // if it doesn't have a real id, it's/was new
        expires_at: itemCurrentState.expires_at ? formatDate(itemCurrentState.expires_at) : null, // ensure format
      };
      if (changeToStage.isNew) delete changeToStage.id; // No ID for new items
      else delete changeToStage.tempId; // No tempId for existing items


      stageChange(changeToStage);

      // If the item being (de)activated is also the one being edited, update edit state
      if (editingItemId === (itemCurrentState.id || itemCurrentState.tempId)) {
        setCurrentItemDataForEdit(prev => ({ ...prev, is_active: newActiveState }));
      }
      setSuccessMessage(`Item marked for ${newActiveState ? 'activation' : 'deactivation'}. Click "Save All Changes".`);
    }
  };


  const handleSaveAll = async () => {
    if (editingItemId && currentItemDataForEdit) {
      applyRowEditChanges();
      setEditingItemId(null);
      setCurrentItemDataForEdit(null);
    }

    if (stagedChanges.length === 0) {
      setSuccessMessage("No changes to save.");
      // setError(null); // Also clear error if displaying this message
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage('');

    const payload = stagedChanges.map(change => {
      // Destructure to easily pick and omit fields
      const {
        isNew,
        tempId,
        _isDeleted, // Internal UI flag, not for backend
        created_at, // Exclude
        updated_at, // Exclude
        ...itemData // Keep the rest of the properties
      } = change;

      // Ensure expires_at is null if empty, or correctly formatted
      itemData.expires_at = itemData.expires_at ? formatDate(itemData.expires_at) : null;

      if (isNew || !itemData.id) {
        // For new items, ensure 'id' is not present in the itemData sent to backend.
        // The backend's batch update logic will assign an ID upon creation.
        delete itemData.id;
      }
      // 'id' for existing items will be part of itemData if it existed on 'change' object

      return itemData; // This itemData now excludes the unwanted fields
    });
    
    try {
      console.log("Sending to batch update:", JSON.stringify(payload, null, 2));
      const response = await batchUpdateBannedEmails(payload);
      setSuccessMessage(`Batch update successful! ${response.data?.length || 0} items processed.`);
      toast({
        variant: 'success',
        title: 'success',
        description: `Batch update successful! ${response.data?.length || 0} items processed.`,
      })
      setStagedChanges([]);
      fetchData(); // Refresh all data
    } catch (err) {
      console.error("Batch Update Error:", err.response?.data || err.message);
      setError(err.response?.data?.detail || `Batch update failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      key: 'email_address', header: 'Email Address',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="email" name="email_address" value={currentItemDataForEdit?.email_address || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-base"/> :
        <span className="text-sm">{item.email_address}</span>
    },
    {
      key: 'reason', header: 'Reason',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="text" name="reason" value={currentItemDataForEdit?.reason || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-base"/> :
        <span className="text-sm">{item.reason || '-'}</span>
    },
    {
      key: 'is_fatal', header: 'Fatal?',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="checkbox" name="is_fatal" checked={!!currentItemDataForEdit?.is_fatal} onChange={handleInputChange} className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"/> :
        <span className="text-sm">{item.is_fatal ? 'Yes' : 'No'}</span>
    },
     { // is_active is primarily controlled by the (De)Activate button, but editable if needed.
      key: 'is_active', header: 'Status', // Changed header to Status
      render: (item) => editingItemId === (item.id || item.tempId) ?
        ( <label className="flex items-center text-sm">
            <input type="checkbox" name="is_active" checked={!!currentItemDataForEdit?.is_active} onChange={handleInputChange} className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/>
            {currentItemDataForEdit?.is_active ? "Active" : "Inactive"}
          </label>
        ) :
        <span className={`text-sm font-semibold px-2 py-1 rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
    },
    {
      key: 'expires_at', header: 'Expires At',
      render: (item) => editingItemId === (item.id || item.tempId) ?
        <input type="date" name="expires_at" value={currentItemDataForEdit?.expires_at || ''} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-base"/> :
        <span className="text-sm">{item.expires_at ? formatDate(item.expires_at) : 'N/A'}</span>
    },
    { key: 'created_at', header: 'Created', render: (item) => <span className="text-sm">{item.isNew && !item.id ? <span className="italic text-gray-400">New (Unsaved)</span> : formatDateTime(item.created_at)}</span> },
    { key: 'updated_at', header: 'Updated', render: (item) => <span className="text-sm">{item.isNew && !item.id ? <span className="italic text-gray-400">N/A</span> : formatDateTime(item.updated_at)}</span> },
  ];

    // DERIVE ACTIVE/INACTIVE LISTS DIRECTLY FROM allBannedEmails
  const activeEmails = allBannedEmails.filter(email => email.is_active === true);
  const inactiveEmails = allBannedEmails.filter(email => email.is_active === false); // Explicitly check for false

  // ... (JSX for rendering, including the two <CrudTable> instances, remains largely the same)
  // Ensure the 'items' prop for CrudTable receives activeEmails and inactiveEmails respectively.
  // The "Save All" button logic for visibility:
  const showSaveAllButton = stagedChanges.length > 0 || editingItemId !== null;

  return (
    <div className="p-4 md:p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200">
        Manage Banned Emails
      </h2>

      {isLoading && <p className="text-blue-600 animate-pulse">Loading data...</p>}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded whitespace-pre-line">
            {typeof error === 'string' ? error : JSON.stringify(error, null, 2)} {/* Display string or stringify object */}
        </div>
      )}      
      {successMessage && <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">{successMessage}</div>}


      {!isAdding && (
        <Button onClick={() => { setIsAdding(true); setError(null); setSuccessMessage(''); handleCancelEdit(); }} variant="primary" size="md" className="mb-6">
          Add New Email
        </Button>
      )}

      {isAdding && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 shadow">
          <h4 className="text-lg font-medium text-gray-700 mb-3">Add New Banned Email</h4>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div className="flex flex-col">
              <label htmlFor="new_email_address" className="mb-1 text-sm font-medium text-gray-600">Email Address*</label>
              <input id="new_email_address" type="email" name="email_address" placeholder="user@example.com" value={newItemData.email_address} onChange={handleInputChange} required className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"/>
            </div>
            <div className="flex flex-col">
              <label htmlFor="new_reason" className="mb-1 text-sm font-medium text-gray-600">Reason</label>
              <input id="new_reason" type="text" name="reason" placeholder="Reason for ban" value={newItemData.reason} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"/>
            </div>
            <div className="flex flex-col">
              <label htmlFor="new_expires_at" className="mb-1 text-sm font-medium text-gray-600">Expires At</label>
              <input id="new_expires_at" type="date" name="expires_at" value={newItemData.expires_at} onChange={handleInputChange} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"/>
            </div>
             <div className="flex items-center space-x-6 mt-2 md:mt-0 md:pt-6">
              <label className="flex items-center text-sm font-medium text-gray-600">
                <input type="checkbox" name="is_fatal" checked={newItemData.is_fatal} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/> Is Fatal
              </label>
              <label className="flex items-center text-sm font-medium text-gray-600">
                <input type="checkbox" name="is_active" checked={newItemData.is_active} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"/> Is Active
              </label>
            </div>
          </div>
          <div className="mt-4 flex gap-x-3">
            <Button onClick={handleAddNewRowToList} variant="green" size="md">Add to List & Stage</Button>
            <Button onClick={() => { setIsAdding(false); setError(null); }} variant="outline" size="md">Cancel</Button>
          </div>
        </div>
      )}

      {showSaveAllButton && ( // Use the new condition for showing save all button
        <div className="my-6 text-right">
          <Button onClick={handleSaveAll} variant="primary" size="lg" isLoading={isLoading} disabled={isLoading || (editingItemId && !currentItemDataForEdit?.email_address)}>
            {isLoading ? 'Saving All...' : `Save All (${stagedChanges.length}) Changes`}
          </Button>
        </div>
      )}

      <section className="mb-8">
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Active Banned Emails ({activeEmails.length})</h3>
        {activeEmails.length > 0 ? (
            <CrudTable
            items={activeEmails}
            columns={columns}
            onDeleteItem={handleDeleteOrDeactivate}
            editingItemId={editingItemId}
            onEditItem={handleEditClick}
            onCancelEdit={handleCancelEdit}
            onApplyEdit={applyRowEditChanges}
            tableClassName="min-w-full divide-y divide-gray-200 shadow border-b border-gray-200 sm:rounded-lg"
            headerClassName="bg-gray-50"
            cellClassName="px-6 py-4 whitespace-nowrap"
            actionCellClassName="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2"
            />
        ) : (<p className="text-gray-500">No active banned emails.</p>)}
      </section>

      <section>
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Inactive Banned Emails ({inactiveEmails.length})</h3>
         {inactiveEmails.length > 0 ? (
            <CrudTable
            items={inactiveEmails}
            columns={columns}
            onDeleteItem={handleDeleteOrDeactivate}
            editingItemId={editingItemId}
            onEditItem={handleEditClick}
            onCancelEdit={handleCancelEdit}
            onApplyEdit={applyRowEditChanges}
            tableClassName="min-w-full divide-y divide-gray-200 shadow border-b border-gray-200 sm:rounded-lg"
            headerClassName="bg-gray-50"
            cellClassName="px-6 py-4 whitespace-nowrap"
            actionCellClassName="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2"
            />
         ) : (<p className="text-gray-500">No inactive banned emails.</p>)}
      </section>
    </div>
  );
};

export default BannedEmailsPage;
